import React, { createContext, useContext, useState, ReactNode } from 'react';

/**
 * Campaign Form Context
 * Provides global state for campaign form fields that can be updated by AI Copy Generator
 */

interface CampaignCopy {
  headline: string;
  description: string;
  cta: string;
}

interface CopyVersion {
  id: string;
  headline: string;
  description: string;
  cta: string;
  appliedAt: Date;
  source: 'ai' | 'manual';
  aiSuggestionId?: string;
}

interface CampaignFormContextType {
  // Current copy
  copy: CampaignCopy;
  setCopy: (copy: CampaignCopy) => void;
  updateField: (field: keyof CampaignCopy, value: string) => void;
  
  // Copy history
  copyHistory: CopyVersion[];
  addToHistory: (version: CopyVersion) => void;
  
  // Apply AI suggestion
  applyCopy: (copy: CampaignCopy, aiSuggestionId?: string) => void;
  
  // Undo/Redo
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  
  // Campaign context
  campaignId?: string;
  setCampaignId: (id: string) => void;
}

const CampaignFormContext = createContext<CampaignFormContextType | undefined>(undefined);

export const useCampaignForm = () => {
  const context = useContext(CampaignFormContext);
  if (!context) {
    throw new Error('useCampaignForm must be used within CampaignFormProvider');
  }
  return context;
};

interface CampaignFormProviderProps {
  children: ReactNode;
  initialCopy?: CampaignCopy;
  campaignId?: string;
}

export const CampaignFormProvider: React.FC<CampaignFormProviderProps> = ({
  children,
  initialCopy = { headline: '', description: '', cta: '' },
  campaignId: initialCampaignId
}) => {
  const [copy, setCopyState] = useState<CampaignCopy>(initialCopy);
  const [copyHistory, setCopyHistory] = useState<CopyVersion[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);
  const [campaignId, setCampaignId] = useState<string | undefined>(initialCampaignId);

  const setCopy = (newCopy: CampaignCopy) => {
    setCopyState(newCopy);
  };

  const updateField = (field: keyof CampaignCopy, value: string) => {
    setCopyState(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addToHistory = (version: CopyVersion) => {
    setCopyHistory(prev => {
      // Remove any versions after current index (if user undid and is making a new change)
      const newHistory = prev.slice(0, historyIndex + 1);
      return [...newHistory, version];
    });
    setHistoryIndex(prev => prev + 1);
  };

  const applyCopy = (newCopy: CampaignCopy, aiSuggestionId?: string) => {
    // Add current state to history before applying new copy
    const version: CopyVersion = {
      id: Date.now().toString(),
      ...newCopy,
      appliedAt: new Date(),
      source: aiSuggestionId ? 'ai' : 'manual',
      aiSuggestionId
    };
    
    addToHistory(version);
    setCopy(newCopy);
  };

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const previousVersion = copyHistory[newIndex];
      setHistoryIndex(newIndex);
      setCopy({
        headline: previousVersion.headline,
        description: previousVersion.description,
        cta: previousVersion.cta
      });
    }
  };

  const redo = () => {
    if (historyIndex < copyHistory.length - 1) {
      const newIndex = historyIndex + 1;
      const nextVersion = copyHistory[newIndex];
      setHistoryIndex(newIndex);
      setCopy({
        headline: nextVersion.headline,
        description: nextVersion.description,
        cta: nextVersion.cta
      });
    }
  };

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < copyHistory.length - 1;

  const value: CampaignFormContextType = {
    copy,
    setCopy,
    updateField,
    copyHistory,
    addToHistory,
    applyCopy,
    canUndo,
    canRedo,
    undo,
    redo,
    campaignId,
    setCampaignId
  };

  return (
    <CampaignFormContext.Provider value={value}>
      {children}
    </CampaignFormContext.Provider>
  );
};
